import type { ReactNode } from 'react';
import { useId, useMemo } from 'react';
import { Area, AreaChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import styled from 'styled-components';
import { Typography } from '../typography';
import { colors } from '../../const';

const themeColors = {
  lightblue: { main: colors['--primary-40'], contrastText: colors['--primary-100'] },
  lightgreen: { main: colors['--green-40'], contrastText: colors['--green-100'] },
  lightyellow: { main: colors['--yellow-40'], contrastText: colors['--yellow-100'] },
  lightpurple: { main: colors['--secondary-40'], contrastText: colors['--secondary-100'] },
  lightred: { main: colors['--red-40'], contrastText: colors['--red-100'] },
};

const accentColorByTheme: Record<keyof typeof themeColors, keyof typeof colors> = {
  lightblue: '--primary-100',
  lightgreen: '--green-100',
  lightyellow: '--yellow-100',
  lightpurple: '--secondary-100',
  lightred: '--red-100',
};

interface LineIconPanelProps {
  title: string;
  icon: ReactNode;
  subValueIconColor?: keyof typeof themeColors;
  color: keyof typeof themeColors;
  value: string | number;
  subValueIcon: ReactNode;
  subValueLeft: string;
  subValueRight: string;
  chartData: number[] | null;
}

const Card = styled.div`
  display: grid;
  grid-template-rows: 1fr max-content;
  grid-auto-flow: row;
  grid-row-gap: var(--spacing-4);
  min-width: 0;

  background: #ffffff;
  border-radius: var(--border-radius-6);
  padding: 1.6rem;
  border: 0.1rem solid ${colors['--gray-50']};
`;

const TopRow = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: start;
  gap: var(--spacing-6);
`;

const IconBox = styled.div<{ $bg: string; $fg: string }>`
  width: 4.8rem;
  height: 4.8rem;
  border-radius: var(--border-radius-6);
  background: ${({ $bg }) => $bg};
  color: ${({ $fg }) => $fg};
  display: grid;
  place-items: center;

  svg {
    font-size: 3rem;
  }
`;

const TextBlock = styled.div`
  display: grid;
  grid-template-rows: max-content 1fr max-content;
  grid-auto-flow: row;
  height: 100%;

  & > *:nth-child(1),
  & > *:nth-child(3) {
    letter-spacing: -0.02rem;
  }

  & > *:nth-child(2) {
    display: grid;
    align-items: center;
  }
`;

const TrendRow = styled.div`
  display: grid;
  grid-auto-flow: column;
  justify-content: start;
  align-items: center;
  column-gap: 0.4rem;
`;

const TrendIconWrap = styled.span<{ $accentKey: keyof typeof colors }>`
  display: inline-grid;
  place-items: center;
  color: ${({ $accentKey }) => colors[$accentKey]};

  svg {
    font-size: 1.8rem;
    margin-right: -0.2rem;
  }
`;

const ChartWrap = styled.div`
  width: 100%;
  min-width: 0;
  height: 5.2rem;
  min-height: 5.2rem;
  box-sizing: border-box;
`;

const SPARKLINE_INITIAL_DIMENSION = { width: 280, height: 52 } as const;

export const LineIconPanel = ({
  title,
  icon,
  subValueIconColor,
  color,
  value,
  subValueIcon,
  subValueLeft,
  subValueRight,
  chartData,
}: LineIconPanelProps) => {
  const palette = themeColors[color];
  const accentKey = subValueIconColor
    ? accentColorByTheme[subValueIconColor]
    : accentColorByTheme[color];
  const gradientId = useId().replace(/:/g, '');
  const gradientFillId = `spark-fill-${gradientId}`;

  const chartRows = useMemo(() => {
    if (!chartData?.length) return [];
    return chartData.map((valuePoint, index) => ({ index, value: valuePoint }));
  }, [chartData]);

  return (
    <Card>
      <TopRow>
        <IconBox $bg={palette.main} $fg={palette.contrastText}>
          {icon}
        </IconBox>
        <TextBlock>
          <Typography variant="textXS" weight="semibold" color="--gray-90" uppercase>
            {title}
          </Typography>
          <Typography variant="h5" weight="bold" color="--gray-100">
            {Number(value).toLocaleString('en-US')}
          </Typography>
          <TrendRow>
            <TrendIconWrap $accentKey={accentKey}>{subValueIcon}</TrendIconWrap>
            <Typography variant="textS" weight="semibold" color={accentKey}>
              {subValueLeft}
            </Typography>
            <Typography variant="textS" weight="normal" color="--gray-70">
              {subValueRight}
            </Typography>
          </TrendRow>
        </TextBlock>
      </TopRow>

      {chartRows.length > 0 ? (
        <ChartWrap>
          <ResponsiveContainer
            width="100%"
            height="100%"
            initialDimension={SPARKLINE_INITIAL_DIMENSION}
          >
            <AreaChart data={chartRows}>
              <defs>
                <linearGradient id={gradientFillId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={palette.contrastText} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={palette.contrastText} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="index" hide />
              <YAxis hide domain={['dataMin', 'dataMax']} />
              <Area
                type="linear"
                dataKey="value"
                stroke={palette.contrastText}
                strokeWidth={2}
                fill={`url(#${gradientFillId})`}
                fillOpacity={1}
                isAnimationActive={false}
                dot={false}
                activeDot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartWrap>
      ) : null}
    </Card>
  );
};
